/**
 * Note lane geometry and analytic hit-testing (W2-03 note-lane; ARCHITECTURE.md §7.7, DESIGN.md §4.6, §5.3-5.4).
 *
 * The lane keeps the notes of one line as a struct of typed arrays sorted by beat (`LaneNoteIndex`, built from
 * `DocIndexes.notesByLine`, which is sorted by (t, x, id)). Every query is a binary search on the beat array followed
 * by an X filter, so its cost depends on what is under the pointer or inside the rectangle, not on the line's size:
 *   - `hitNote`: the note (and hold part: head, tail or body) under a point, with pixel tolerances;
 *   - `notesInRect`: notes whose glyph intersects a marquee (beats f64, lane px), for `itemsInRect`;
 *   - `visibleSlice`: the index range the content layer draws (held-over holds included through `maxSpan`);
 *   - `stackAt`: notes at the same spot (same beat, same X) for count badges and Alt+click cycling.
 *
 * Horizontal geometry: the lane draws the line unrotated, X in RPE px (the play field is -675..675). `LaneXMap`
 * maps RPE X to lane px for the current half-range and centre (lane X zoom and pan). A note's glyph is as wide as
 * Phira draws it relative to the field: 2 · 0.13175016 world units per unit of size (R§1: note full width), i.e.
 * about 177.86 RPE px at size 1, never narrower than MIN_GLYPH_PX on screen.
 */

import { Search } from '../../core/math/search';
import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { NoteId, NoteKind } from '../../core/model/types';

// ---------------------------------------------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------------------------------------------

/** Half the play field width in RPE px (X of the field edges). */
export const FIELD_HALF_PX = 675;
/** Note glyph width in RPE px per unit of size (Phira: 2 · 0.13175016 world units, 1350 RPE px = 2 world units). */
export const NOTE_WIDTH_RPE = 2 * 0.13175016 * FIELD_HALF_PX;
/** Narrowest glyph drawn, in CSS px (far zoomed-out lanes). */
export const MIN_GLYPH_PX = 10;
/** Glyph thickness along the time axis, CSS px. */
export const GLYPH_H: Readonly<Record<NoteKind, number>> = { tap: 8, drag: 5, flick: 8, hold: 8 };
/** Distance from a hold's head / tail (px) that grabs the handle instead of the body. */
export const HANDLE_PX = 6;
/** Extra pointer tolerance around a glyph, CSS px. */
export const HIT_PAD_X = 3;
export const HIT_PAD_Y = 4;
/** Two notes closer than this in X (RPE px) at the same beat are "at the same spot" (count badge). */
export const STACK_EPS_X = 1;

/** Numeric kind codes of the index. */
export const KIND_CODE: Readonly<Record<NoteKind, number>> = { tap: 0, drag: 1, flick: 2, hold: 3 };
export const KIND_OF_CODE: readonly NoteKind[] = ['tap', 'drag', 'flick', 'hold'];

/** Flag bits of the index. */
export const FLAG_FAKE = 1;
export const FLAG_BELOW = 2;
/** Fully transparent note (alpha 0): drawn as an outline only. */
export const FLAG_TRANSPARENT = 4;

// ---------------------------------------------------------------------------------------------------------------
// Horizontal mapping
// ---------------------------------------------------------------------------------------------------------------

/** RPE X <-> lane px for one lane width. */
export interface LaneXMap {
  /** RPE X at the lane's centre. */
  readonly center: number;
  /** Visible half-range in RPE px. */
  readonly half: number;
  /** Lane width in CSS px. */
  readonly width: number;
  /** CSS px per RPE px. */
  readonly scale: number;
  toPx(x: number): number;
  toX(px: number): number;
}

export function laneXMap(center: number, half: number, width: number): LaneXMap {
  const h = Number.isFinite(half) && half > 0 ? half : FIELD_HALF_PX;
  const c = Number.isFinite(center) ? center : 0;
  const w = Math.max(1, width);
  const scale = w / (2 * h);
  return {
    center: c,
    half: h,
    width: w,
    scale,
    toPx: (x) => w / 2 + (x - c) * scale,
    toX: (px) => c + (px - w / 2) / scale,
  };
}

/** Half the glyph width in CSS px for a note size. */
export function glyphHalfPx(size: number, xmap: LaneXMap): number {
  const s = Number.isFinite(size) ? Math.abs(size) : 1;
  return Math.max(MIN_GLYPH_PX, NOTE_WIDTH_RPE * s * xmap.scale) / 2;
}

/** Zoom the lane X range by `factor` (> 1 zooms in) keeping RPE X under lane px `anchorPx` fixed. */
export function zoomXRange(xmap: LaneXMap, factor: number, anchorPx: number): { center: number; half: number } {
  const f = Number.isFinite(factor) && factor > 0 ? factor : 1;
  const anchorX = xmap.toX(anchorPx);
  const half = xmap.half / f;
  const scale = xmap.width / (2 * half);
  const center = anchorX - (anchorPx - xmap.width / 2) / scale;
  return { center, half };
}

/** Pan the lane X range by `dxPx` CSS px (positive moves the view right, content left). */
export function panXRange(xmap: LaneXMap, dxPx: number): { center: number; half: number } {
  return { center: xmap.center + dxPx / xmap.scale, half: xmap.half };
}

// ---------------------------------------------------------------------------------------------------------------
// Note index
// ---------------------------------------------------------------------------------------------------------------

/** The note fields the lane reads (a model `Note` satisfies it). */
export interface LaneNoteLike {
  readonly id: NoteId;
  readonly kind: NoteKind;
  readonly t: Beat;
  readonly end: Beat;
  readonly x: number;
  readonly size: number;
  readonly fake: boolean;
  readonly above: boolean;
  readonly alpha: number;
}

/** Notes of one line as typed arrays sorted by beat (ascending; ties keep the input order). */
export interface LaneNoteIndex {
  readonly count: number;
  readonly ids: Int32Array;
  /** Start beat (f64). */
  readonly t: Float64Array;
  /** End beat (f64; = t except holds). */
  readonly end: Float64Array;
  readonly x: Float64Array;
  readonly size: Float32Array;
  readonly kind: Uint8Array;
  readonly flags: Uint8Array;
  /** Longest hold in beats (0 without holds): queries look this far before their range for held-over holds. */
  readonly maxSpan: number;
  /** Position of a note in the arrays, or -1. */
  indexOf(id: NoteId): number;
}

export const EMPTY_NOTE_INDEX: LaneNoteIndex = buildLaneNoteIndex([]);

/** Builds the index; notes are sorted by beat when the input is not already (stable). */
export function buildLaneNoteIndex(notes: readonly LaneNoteLike[]): LaneNoteIndex {
  const n = notes.length;
  const beats = new Float64Array(n);
  let sorted = true;
  for (let i = 0; i < n; i++) {
    beats[i] = Beats.toF64((notes[i] as LaneNoteLike).t);
    if (i > 0 && (beats[i] as number) < (beats[i - 1] as number)) sorted = false;
  }
  let order: number[] | null = null;
  if (!sorted) {
    order = Array.from({ length: n }, (_, i) => i);
    order.sort((a, b) => (beats[a] as number) - (beats[b] as number) || a - b);
  }
  const ids = new Int32Array(n);
  const t = new Float64Array(n);
  const end = new Float64Array(n);
  const x = new Float64Array(n);
  const size = new Float32Array(n);
  const kind = new Uint8Array(n);
  const flags = new Uint8Array(n);
  let maxSpan = 0;
  for (let i = 0; i < n; i++) {
    const src = order === null ? i : (order[i] as number);
    const note = notes[src] as LaneNoteLike;
    ids[i] = note.id;
    const tb = beats[src] as number;
    t[i] = tb;
    const eb = note.kind === 'hold' ? Beats.toF64(note.end) : tb;
    end[i] = eb;
    if (eb - tb > maxSpan) maxSpan = eb - tb;
    x[i] = note.x;
    size[i] = note.size;
    kind[i] = KIND_CODE[note.kind];
    flags[i] = (note.fake ? FLAG_FAKE : 0) | (note.above ? 0 : FLAG_BELOW) | (note.alpha <= 0 ? FLAG_TRANSPARENT : 0);
  }
  let byId: Map<number, number> | null = null;
  return {
    count: n,
    ids,
    t,
    end,
    x,
    size,
    kind,
    flags,
    maxSpan,
    indexOf(id) {
      if (byId === null) {
        byId = new Map();
        for (let i = 0; i < n; i++) byId.set(ids[i] as number, i);
      }
      return byId.get(id) ?? -1;
    },
  };
}

/** Index range [first, end) of the notes that can be visible in [beat0, beat1] (holds started earlier included). */
export function visibleSlice(index: LaneNoteIndex, beat0: number, beat1: number): readonly [number, number] {
  const lo = Math.min(beat0, beat1);
  const hi = Math.max(beat0, beat1);
  const first = Search.lowerBound(index.t, lo - index.maxSpan);
  const last = Search.upperBound(index.t, hi, first);
  return [first, last];
}

/** True when note i overlaps the beat range [lo, hi]. */
export function overlapsBeats(index: LaneNoteIndex, i: number, lo: number, hi: number): boolean {
  return (index.t[i] as number) <= hi && (index.end[i] as number) >= lo;
}

// ---------------------------------------------------------------------------------------------------------------
// Hit-testing
// ---------------------------------------------------------------------------------------------------------------

/** Vertical mapping the queries need (a TimeAxis satisfies it). */
export interface BeatAxis {
  beatAtY(y: number): number;
  yAtBeat(beat: number): number;
  readonly pxPerBeat: number;
}

export type LaneHitPart = 'body' | 'head' | 'tail';

export interface LaneNoteHit {
  /** Position in the index. */
  readonly index: number;
  readonly id: NoteId;
  readonly part: LaneHitPart;
  /** Notes at the same spot (>= 1). */
  readonly stack: number;
}

export interface HitOptions {
  /** Notes that may be hit (e.g. the sides filter); all when absent. */
  readonly include?: (i: number) => boolean;
  /** Extra tolerance in px (touch). */
  readonly slop?: number;
}

/**
 * The note under (x, y) in lane px, or null. Among overlapping candidates the closest wins; hold bodies yield to
 * any note glyph drawn over them, and later notes (drawn on top) win ties.
 */
export function hitNote(
  index: LaneNoteIndex,
  axis: BeatAxis,
  xmap: LaneXMap,
  x: number,
  y: number,
  opts: HitOptions = {},
): LaneNoteHit | null {
  if (index.count === 0) return null;
  const slop = opts.slop ?? 0;
  const padY = HIT_PAD_Y + slop;
  const padX = HIT_PAD_X + slop;
  const maxH = 8;
  const tolBeats = (maxH / 2 + padY) / Math.max(axis.pxPerBeat, 1e-9);
  const beat = axis.beatAtY(y);
  const [first, last] = visibleSlice(index, beat - tolBeats, beat + tolBeats);
  let best = -1;
  let bestPart: LaneHitPart = 'body';
  let bestScore = Number.POSITIVE_INFINITY;
  for (let i = first; i < last; i++) {
    if (opts.include !== undefined && !opts.include(i)) continue;
    const xc = xmap.toPx(index.x[i] as number);
    const hw = glyphHalfPx(index.size[i] as number, xmap);
    const dx = Math.abs(x - xc);
    if (dx > hw + padX) continue;
    const kind = index.kind[i] as number;
    const yHead = axis.yAtBeat(index.t[i] as number);
    let score: number;
    let part: LaneHitPart = 'body';
    if (kind === KIND_CODE.hold) {
      const yTail = axis.yAtBeat(index.end[i] as number);
      const top = Math.min(yHead, yTail) - padY;
      const bottom = Math.max(yHead, yTail) + padY;
      if (y < top || y > bottom) continue;
      const dHead = Math.abs(y - yHead);
      const dTail = Math.abs(y - yTail);
      if (dHead <= HANDLE_PX && dHead <= dTail) {
        part = 'head';
        score = dHead;
      } else if (dTail <= HANDLE_PX) {
        part = 'tail';
        score = dTail;
      } else {
        score = 6 + Math.min(dHead, dTail) * 0.01;
      }
    } else {
      const hh = GLYPH_H[KIND_OF_CODE[kind] ?? 'tap'] / 2 + padY;
      const dy = Math.abs(y - yHead);
      if (dy > hh) continue;
      score = dy;
    }
    score += (dx / Math.max(hw, 1)) * 2;
    if (score <= bestScore) {
      bestScore = score;
      best = i;
      bestPart = part;
    }
  }
  if (best < 0) return null;
  return { index: best, id: index.ids[best] as NoteId, part: bestPart, stack: stackAt(index, best).length };
}

/**
 * Notes whose glyph intersects the rectangle [beat0, beat1] x [x0, x1] (beats f64, lane px; any corner order).
 * Holds count when any part of their body is inside.
 */
export function notesInRect(
  index: LaneNoteIndex,
  xmap: LaneXMap,
  beat0: number,
  beat1: number,
  x0: number,
  x1: number,
  include?: (i: number) => boolean,
): NoteId[] {
  const lo = Math.min(beat0, beat1);
  const hi = Math.max(beat0, beat1);
  const left = Math.min(x0, x1);
  const right = Math.max(x0, x1);
  const [first, last] = visibleSlice(index, lo, hi);
  const out: NoteId[] = [];
  for (let i = first; i < last; i++) {
    if (!overlapsBeats(index, i, lo, hi)) continue;
    if (include !== undefined && !include(i)) continue;
    const xc = xmap.toPx(index.x[i] as number);
    const hw = glyphHalfPx(index.size[i] as number, xmap);
    if (xc + hw < left || xc - hw > right) continue;
    out.push(index.ids[i] as NoteId);
  }
  return out;
}

/** Indices of the notes at the same spot as note i (same beat, X within STACK_EPS_X), ascending. */
export function stackAt(index: LaneNoteIndex, i: number): number[] {
  if (i < 0 || i >= index.count) return [];
  const t = index.t[i] as number;
  const x = index.x[i] as number;
  let a = i;
  while (a > 0 && index.t[a - 1] === t) a--;
  let b = i + 1;
  while (b < index.count && index.t[b] === t) b++;
  const out: number[] = [];
  for (let k = a; k < b; k++) if (Math.abs((index.x[k] as number) - x) < STACK_EPS_X) out.push(k);
  return out;
}

/** Ids of the notes between two beats (inclusive, any order) passing `include` (Shift+click range selection). */
export function notesBetween(
  index: LaneNoteIndex,
  beatA: number,
  beatB: number,
  include?: (i: number) => boolean,
): NoteId[] {
  const lo = Math.min(beatA, beatB);
  const hi = Math.max(beatA, beatB);
  const first = Search.lowerBound(index.t, lo);
  const last = Search.upperBound(index.t, hi, first);
  const out: NoteId[] = [];
  for (let i = first; i < last; i++) if (include === undefined || include(i)) out.push(index.ids[i] as NoteId);
  return out;
}

/** Beat extent [min t, max end] and X extent of a set of notes of the index (null when none is in it). */
export function extentOf(
  index: LaneNoteIndex,
  ids: Iterable<NoteId>,
): {
  readonly b0: number;
  readonly b1: number;
  readonly x0: number;
  readonly x1: number;
  readonly count: number;
} | null {
  let b0 = Number.POSITIVE_INFINITY;
  let b1 = Number.NEGATIVE_INFINITY;
  let x0 = Number.POSITIVE_INFINITY;
  let x1 = Number.NEGATIVE_INFINITY;
  let count = 0;
  for (const id of ids) {
    const i = index.indexOf(id);
    if (i < 0) continue;
    count++;
    b0 = Math.min(b0, index.t[i] as number);
    b1 = Math.max(b1, index.end[i] as number);
    x0 = Math.min(x0, index.x[i] as number);
    x1 = Math.max(x1, index.x[i] as number);
  }
  return count === 0 ? null : { b0, b1, x0, x1, count };
}
